import React, { createContext, useContext } from "react";
import type { PagesJumpMap } from "@eaco/forms-core";

interface ValidationContextValue {
  /** Element ids that have a failing required-check after a submit attempt. */
  invalidIds: Set<string>;
  /** Element id we want to scroll into view (refreshed each submit attempt). */
  focusElementId: string | null;
  /**
   * Monotonic counter bumped each time a submit attempt fails. Lets
   * focus-aware components (e.g. Pages) react to a fresh validation
   * pass even if the focus element id hasn't changed.
   */
  focusToken: number;
  /** Pages element id → page index to switch to (for jumps). */
  pageJumps: PagesJumpMap;
  /**
   * Element ids that are required but currently unsatisfied — recomputed
   * *live* (every value change), unlike `invalidIds` which only populates
   * after a submit attempt. Drives the per-page "unanswered required"
   * indicator in the page stepper. Empty when the host doesn't supply it.
   */
  incompleteElementIds: Set<string>;
  /**
   * Form-level "every question required" flag. When true, per-field
   * `required` config is overridden — so the OPTIONAL tag is suppressed
   * everywhere. Defaults false.
   */
  requiredOverride: boolean;
}

const ValidationContext = createContext<ValidationContextValue>({
  invalidIds: new Set(),
  focusElementId: null,
  focusToken: 0,
  pageJumps: new Map(),
  incompleteElementIds: new Set(),
  requiredOverride: false,
});

const EMPTY_SET: Set<string> = new Set();

export const ValidationProvider: React.FC<
  Omit<ValidationContextValue, "incompleteElementIds" | "requiredOverride"> & {
    incompleteElementIds?: Set<string>;
    requiredOverride?: boolean;
    children: React.ReactNode;
  }
> = ({
  invalidIds,
  focusElementId,
  focusToken,
  pageJumps,
  incompleteElementIds,
  requiredOverride = false,
  children,
}) => (
  <ValidationContext.Provider
    value={{
      invalidIds,
      focusElementId,
      focusToken,
      pageJumps,
      incompleteElementIds: incompleteElementIds ?? EMPTY_SET,
      requiredOverride,
    }}
  >
    {children}
  </ValidationContext.Provider>
);

export const useValidation = () => useContext(ValidationContext);
